import React, { useCallback } from 'react';
import { NavLink } from 'react-router-dom';
import { baseURL } from '../../../api/api';
import { educationalApi } from '../../api/endpoints';
import DataTable from '../../components/DataTable';
import useAdminList from '../../hooks/useAdminList';
import { useUi } from '../../context/UiContext';
import styles from '../listPage.module.css';

const EducationalList = () => {
    const { toast, confirm } = useUi();
    const list = useAdminList(educationalApi.list, { sort: 'id', dir: 'desc' });
    const { reload } = list;

    const handleDelete = useCallback(
        async (row) => {
            const ok = await confirm({
                title: 'Удалить учебное издание?',
                message: `«${row.name}»\n\nИздание, документ и обложка будут удалены безвозвратно.`,
                confirmLabel: 'Удалить',
                danger: true,
            });
            if (!ok) return;

            try {
                await educationalApi.remove(row.id);
                toast.success('Издание удалено');
                reload();
            } catch (e) {
                toast.error(e.message);
            }
        },
        [confirm, toast, reload]
    );

    const columns = [
        { key: 'id', title: 'ID', width: 70, sortable: true },
        {
            key: 'name',
            title: 'Название',
            sortable: true,
            render: (row) => (
                <div>
                    <NavLink to={`/admin/educational/${row.id}`} className={styles.primaryLink}>
                        {row.name || <span className="adm-muted">без названия</span>}
                    </NavLink>
                    {row.authors.length > 0 && (
                        <div className={styles.subline}>{row.authors.map((a) => a.name).join(', ')}</div>
                    )}
                </div>
            ),
        },
        {
            key: 'departments',
            title: 'Кафедры и направления',
            width: 260,
            render: (row) => (
                <div>
                    {row.departments.map((d) => (
                        <span key={d.id} className="adm-tag">
                            {d.name}
                        </span>
                    ))}
                    {row.categories.map((c) => (
                        <span key={`c${c.id}`} className="adm-tag">
                            {c.name}
                        </span>
                    ))}
                    {row.departments.length === 0 && row.categories.length === 0 && (
                        <span className="adm-muted">—</span>
                    )}
                </div>
            ),
        },
        {
            key: 'date',
            title: 'Дата',
            width: 110,
            sortable: true,
            render: (row) => row.date || <span className="adm-muted">—</span>,
        },
        {
            key: 'file',
            title: 'Файл',
            width: 90,
            render: (row) =>
                row.file ? (
                    <a href={baseURL + row.file} target="_blank" rel="noopener noreferrer">
                        PDF ↗
                    </a>
                ) : (
                    <span className="adm-badge adm-badge-warning">нет</span>
                ),
        },
        {
            key: 'hide',
            title: 'Доступ',
            width: 130,
            render: (row) => (
                <span className={`adm-badge ${row.hide === 1 ? 'adm-badge-success' : 'adm-badge-neutral'}`}>
                    {row.hide === 1 ? 'открытый' : 'только просмотр'}
                </span>
            ),
        },
        {
            key: 'actions',
            title: '',
            width: 150,
            align: 'right',
            render: (row) => (
                <div className={styles.rowActions}>
                    <NavLink to={`/admin/educational/${row.id}`} className="adm-btn adm-btn-ghost adm-btn-sm">
                        Изменить
                    </NavLink>
                    <button
                        type="button"
                        className="adm-btn adm-btn-danger adm-btn-sm"
                        onClick={() => handleDelete(row)}
                    >
                        Удалить
                    </button>
                </div>
            ),
        },
    ];

    return (
        <>
            <div className="adm-page-header">
                <div>
                    <h1 className="adm-page-title">Учебные издания</h1>
                    <p className="adm-page-subtitle">Учебно-методические пособия по кафедрам</p>
                </div>
                <NavLink to="/admin/educational/new" className="adm-btn adm-btn-primary">
                    + Добавить издание
                </NavLink>
            </div>

            <DataTable
                columns={columns}
                rows={list.rows}
                loading={list.loading}
                error={list.error}
                onRetry={list.reload}
                search={list.search}
                onSearchChange={list.setSearch}
                searchPlaceholder="Поиск по названию, издательству, аннотации"
                sort={list.sort}
                dir={list.dir}
                onSortChange={list.toggleSort}
                page={list.page}
                perPage={list.perPage}
                total={list.total}
                lastPage={list.lastPage}
                onPageChange={list.setPage}
                onPerPageChange={list.setPerPage}
                emptyText={list.search ? 'По запросу ничего не найдено' : 'Изданий пока нет'}
                filters={
                    <select
                        className={styles.filterSelect}
                        value={list.filters.hide === undefined ? '' : list.filters.hide}
                        onChange={(e) => list.setFilter('hide', e.target.value)}
                        aria-label="Фильтр по типу доступа"
                    >
                        <option value="">Любой доступ</option>
                        <option value="1">Открытый доступ</option>
                        <option value="0">Только просмотр</option>
                    </select>
                }
            />
        </>
    );
};

export default EducationalList;
